import ThemedSelect from "../shared/ThemedSelect.jsx";
import { useEffect, useState } from "react";
import { Badge, Button, Card, Col, Form, Row, Table } from "react-bootstrap";
import {
  createUser,
  deleteUser,
  getCurrentUser,
  listUsers,
  mimicUser,
  setUserManages,
  updateUser,
} from "../../services/apiClient.js";

const emptyForm = {
  full_name: "",
  username: "",
  email: "",
  password: "",
  is_admin: false,
  is_active: true,
  is_supervisor: false,
  shown_in_project_coordinator: true,
  is_project_coordinator: true,
  can_mimic: false,
  member_of: "",
  managed_by: "",
  shown_progress_to_from: false,
  direct_dial: "",
  lbp_number: "",
  lbp_class: "",
};

// The known groups (legacy SecurityHelper) — picked from a dropdown, never typed.
// Unknown groups arriving via import still render as removable chips.
const AVAILABLE_GROUPS = ["Administrator", "PowerUser", "Receptionist", "Tender", "Inspector"];

const parseGroups = (memberOf) =>
  String(memberOf || "").split(";").map((g) => g.trim()).filter(Boolean);

// The LBP classes GPS actually uses, offered as a datalist rather than a closed
// dropdown: the national set is longer than this (Site 1-3, Design 1-3) and an
// account imported with a class we have not listed must still be editable.
const LBP_CLASSES = [
  "Carpentry", "Roofing", "External Plastering", "Bricklaying and Blocklaying",
  "Foundations", "Site 1", "Site 2", "Site 3", "Design 1", "Design 2", "Design 3",
];

// WHAT THE LIST SHOWS ABOUT A PERSON, as badges in one column rather than a
// column of Yes/No per flag (Robert 2026-08-03). Admin came out entirely — the
// Administrator group badge already says it, and two columns for one fact is how
// they drift apart. Supervisor and Project Co-Ordinator became badges because
// "No" in a column costs a column's width to tell you nothing.
//
// Extensible on purpose: the next flag worth seeing at a glance is one entry
// here, not another <th>/<td> pair threaded through the table.
function otherInfo(user) {
  const badges = [];
  if (user.is_supervisor) badges.push({ key: "sup", label: "Supervisor", bg: "info" });
  if (user.is_project_coordinator) badges.push({ key: "pco", label: "Project Co-Ordinator", bg: "primary" });
  if (user.lbp_number) {
    badges.push({
      key: "lbp",
      label: `LBP${user.lbp_number}${user.lbp_class ? ` ${user.lbp_class}` : ""}`,
      bg: "success",
    });
  }
  // Only worth saying when it is the exception — with "Show inactive records"
  // off, every row is active and the badge would be noise on all of them.
  if (!user.is_active) badges.push({ key: "off", label: "Inactive", bg: "secondary" });
  if (user.is_locked) badges.push({ key: "lock", label: "Locked", bg: "danger" });
  return badges;
}

// `includeInactive` mirrors the "Show inactive records" checkbox every other
// System Data view has, and 1.0's own users page has had all along. Without it
// the list grew every time somebody left, and 2.0 had no way at all to hide an
// account it had switched off.
//
// Filtered for DISPLAY only — `users` stays complete, because the Manager column
// looks a manager up in it and a manager who has left would otherwise read "-"
// for everyone reporting to them.
function ManageUsers({ includeInactive = false }) {
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [mode, setMode] = useState("list");
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState(emptyForm);
  const [managesIds, setManagesIds] = useState([]);
  const [error, setError] = useState("");

  async function refreshUsers() {
    const [current, rows] = await Promise.all([getCurrentUser(), listUsers()]);
    setMe(current);
    setUsers(rows);
  }

  useEffect(() => {
    refreshUsers().catch((err) => {
      setError(err.message || "Unable to load users.");
    });
  }, []);

  const visibleUsers = includeInactive ? users : users.filter((u) => u.is_active);
  // Who reports to the person being edited, STAGED like the Groups chips: added
  // and removed in the form, written on Save. Seeded from the list already in
  // hand — it is the other side of everyone else's Managed By, so asking the
  // server for it separately could only produce a second answer to disagree with.
  const manages = users.filter((u) => managesIds.includes(u.id));
  const manageable = users.filter(
    (u) => (!editingUser || u.id !== editingUser.id) && !managesIds.includes(u.id)
  );
  const managerNameOf = (u) => users.find((x) => x.id === u.managed_by)?.full_name || null;

  function handleChange(event) {
    const { name, value, type, checked } = event.target;
    setUserForm((current) => ({
      ...current,
      [name]: type === "checkbox" ? checked : value,
    }));
  }

  function addGroup(group) {
    if (!group) return;
    const groups = parseGroups(userForm.member_of);
    if (!groups.some((g) => g.toLowerCase() === group.toLowerCase())) groups.push(group);
    setUserForm((current) => ({ ...current, member_of: groups.join(";") }));
  }

  function removeGroup(group) {
    const groups = parseGroups(userForm.member_of).filter((g) => g !== group);
    setUserForm((current) => ({ ...current, member_of: groups.join(";") }));
  }

  function startAdd() {
    setError("");
    setUserForm(emptyForm);
    setEditingUser(null);
    setMode("add");
  }

  function startEdit(user) {
    setError("");
    setEditingUser(user);
    setUserForm({
      full_name: user.full_name,
      username: user.username,
      email: user.email || "",
      password: "",
      is_admin: user.is_admin,
      is_active: user.is_active,
      is_supervisor: user.is_supervisor,
      shown_in_project_coordinator: !!user.shown_in_project_coordinator,
      is_project_coordinator: !!user.is_project_coordinator,
      can_mimic: !!user.can_mimic,
      member_of: user.member_of || "",
      managed_by: user.managed_by || "",
      shown_progress_to_from: !!user.shown_progress_to_from,
      direct_dial: user.direct_dial || "",
      // Loaded, or opening a licensed inspector's record and pressing Save would
      // quietly wipe the licence off the quotes they sign.
      lbp_number: user.lbp_number || "",
      lbp_class: user.lbp_class || "",
    });
    setManagesIds(users.filter((u) => u.managed_by === user.id).map((u) => u.id));
    setMode("edit");
  }

  async function saveAdd() {
    try {
      const user = await createUser(userForm);
      setUsers((current) => [...current, user]);
      setMode("list");
    } catch (err) {
      setError(err.response?.data?.error || "Unable to add user.");
    }
  }

  async function saveEdit() {
    if (!editingUser) return;

    try {
      const user = await updateUser(editingUser.id, userForm);
      // Manages writes OTHER people's rows, so it is its own call and its own
      // transaction. Sent second: if it fails, the edit to this user has still
      // landed and the error names the part that did not, rather than leaving
      // the whole save ambiguous.
      const before = users.filter((u) => u.managed_by === editingUser.id).map((u) => u.id);
      const changed = before.length !== managesIds.length
        || before.some((id) => !managesIds.includes(id));
      let reports = null;
      if (changed) reports = await setUserManages(editingUser.id, managesIds);

      setUsers((current) =>
        current.map((row) => {
          if (row.id === user.id) return user;
          if (!reports) return row;
          // Reflect the reassignment on the people who moved, so the list and the
          // next edit are right without a refetch.
          const nowManaged = reports.some((r) => r.id === row.id);
          if (nowManaged) return { ...row, managed_by: editingUser.id };
          if (row.managed_by === editingUser.id) return { ...row, managed_by: null };
          return row;
        })
      );
      setMode("list");
      setEditingUser(null);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to update user.");
    }
  }

  async function removeUser(user) {
    if (!window.confirm(`Delete ${user.full_name}?`)) return;

    try {
      await deleteUser(user.id);
      setUsers((current) => current.filter((row) => row.id !== user.id));
    } catch (err) {
      setError(err.response?.data?.error || "Unable to delete user.");
    }
  }

  async function unlockUser(user) {
    try {
      const updated = await updateUser(user.id, { unlock: true });
      setUsers((current) => current.map((row) => (row.id === updated.id ? updated : row)));
    } catch (err) {
      setError(err.response?.data?.error || "Unable to unlock user.");
    }
  }

  // Straight off the Actions column, because turning accounts on and off is a
  // LIST-shaped job and opening each account in turn to flip one switch is the
  // pain (Robert 2026-08-12). Deactivating never touches the jobs the account
  // already holds: those keep the name, and the job's own dropdown keeps showing
  // it — see utils/people.js. It only removes them from being CHOSEN again, and
  // from search and report filters.
  async function setUserActive(user, active) {
    try {
      const updated = await updateUser(user.id, { is_active: active });
      setUsers((current) => current.map((row) => (row.id === updated.id ? updated : row)));
    } catch (err) {
      setError(err.response?.data?.error || `Unable to ${active ? "activate" : "deactivate"} user.`);
    }
  }

  // System Owner only: become this user for the session (debugging).
  // Full reload so every page, permission and theme resets as them.
  async function startMimic(user) {
    try {
      await mimicUser(user.id);
      window.location.assign("/");
    } catch (err) {
      setError(err.response?.data?.error || "Unable to mimic user.");
    }
  }

  const isForm = mode === "add" || mode === "edit";
  const saveDisabled =
    !userForm.full_name ||
    !userForm.username ||
    (mode === "add" && !userForm.password);

  return (
    <>
      {error ? <div className="alert alert-danger">{error}</div> : null}

      {!isForm ? (
        <>
          <div className="d-flex justify-content-between align-items-center mb-2">
            <h5 className="mb-0">System Users</h5>
            {me?.is_admin ? (
              <Button size="sm" onClick={startAdd}>
                Add User
              </Button>
            ) : null}
          </div>
          <Table striped bordered hover responsive size="sm" className="mb-0 align-middle">
            <thead className="table-surface">
              <tr>
                <th>Display Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Groups</th>
                <th>Managed By</th>
                {/* Which Bootswatch theme people actually pick — Robert wants to
                    see what they like before deciding what to design against. */}
                <th>Theme</th>
                <th>Other Info</th>
                <th className="text-end" style={{ width: 260 }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {visibleUsers.map((user) => (
                <tr key={user.id}>
                  <td>
                    {user.full_name}
                    {/* The owner concept is INVISIBLE below owner (same rule as
                        owner-only entities) — an admin seeing the badge reads it
                        as a role they should have (Karen 2026-07-16). */}
                    {user.is_system_owner && me?.is_system_owner ? (
                      <Badge bg="dark" className="ms-2">System Owner</Badge>
                    ) : null}
                  </td>
                  <td>{user.username}</td>
                  <td>{user.email || "-"}</td>
                  <td>
                    {parseGroups(user.member_of).map((g) => (
                      <Badge key={g} bg="secondary" className="me-1">{g}</Badge>
                    ))}
                    {parseGroups(user.member_of).length === 0 ? "-" : null}
                  </td>
                  <td>{users.find((u) => u.id === user.managed_by)?.full_name || "-"}</td>
                  <td className="text-capitalize">{user.theme || <span className="text-muted">default</span>}</td>
                  <td style={{ minWidth: 200 }}>
                    {otherInfo(user).map((b) => (
                      <Badge key={b.key} bg={b.bg} className="me-1 mb-1">{b.label}</Badge>
                    ))}
                    {otherInfo(user).length === 0 ? <span className="text-muted">-</span> : null}
                  </td>
                  <td className="text-end">
                    {me?.is_admin && user.is_locked ? (
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() => unlockUser(user)}
                        data-hint="Clear the failed-login lock without changing the password"
                      >
                        Unlock
                      </Button>
                    ) : null}
                    {/* Owner or a can_mimic grant. The owner's own row shows
                        the button GREYED with a neutral tooltip (like Edit/
                        Delete) — the owner concept stays invisible; the
                        server refuses the owner as a target regardless. */}
                    {(me?.is_system_owner || me?.can_mimic) && me.id !== user.id ? (
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="me-2"
                        onClick={() => startMimic(user)}
                        disabled={user.is_system_owner}
                        data-hint={
                          user.is_system_owner
                            ? "Not available for this account"
                            : "See ClaimsDesk as this user"
                        }
                      >
                        Mimic
                      </Button>
                    ) : null}
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="me-2"
                      onClick={() => startEdit(user)}
                      disabled={user.is_system_owner && !me?.is_system_owner}
                      data-hint={user.is_system_owner && !me?.is_system_owner ? "This account can't be changed here" : undefined}
                    >
                      Edit
                    </Button>
                    {me?.is_admin ? (
                      <Button
                        variant={user.is_active ? "outline-warning" : "outline-success"}
                        size="sm"
                        className="me-2"
                        onClick={() => setUserActive(user, !user.is_active)}
                        disabled={me.id === user.id || (user.is_system_owner && !me?.is_system_owner)}
                        data-hint={
                          me.id === user.id
                            ? "You can't deactivate your own account"
                            : user.is_system_owner && !me?.is_system_owner
                              ? "This account can't be changed here"
                              : user.is_active
                                ? "Take this account out of the assignment dropdowns and search filters. Jobs they already hold keep their name."
                                : "Put this account back in the dropdowns"
                        }
                      >
                        {user.is_active ? "Deactivate" : "Activate"}
                      </Button>
                    ) : null}
                    {me?.is_admin ? (
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => removeUser(user)}
                        disabled={me.id === user.id || user.is_system_owner}
                        data-hint={
                          user.is_system_owner
                            ? me?.is_system_owner
                              ? "The system owner can't be deleted"
                              : "This account can't be deleted here"
                            : undefined
                        }
                      >
                        Delete
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </>
      ) : (
        <div style={{ maxWidth: 900 }}>
          {/* SECTIONS, and a width (Robert 2026-08-03: "ugly as"). It was one flat
              column at full page width, so every field stretched across a 2000px
              screen and eleven controls sat in a single strip with nothing saying
              which belonged together.
              The checkboxes were the worst of it because they answered two
              different questions in one pile: what somebody IS (Supervisor,
              Project Co-Ordinator) and which DROPDOWNS they appear in. Splitting
              those is most of what makes this readable. */}
          <h6 className="mb-3">{mode === "add" ? "Add New User" : "Edit User"}</h6>
          <Form>
            <Card className="mb-3">
              <Card.Body>
                <Card.Title className="h6 text-uppercase text-muted small">Person</Card.Title>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label>Display Name</Form.Label>
                    <Form.Control name="full_name" value={userForm.full_name} onChange={handleChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Username</Form.Label>
                    <Form.Control name="username" value={userForm.username} onChange={handleChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Email</Form.Label>
                    <Form.Control name="email" type="email" value={userForm.email} onChange={handleChange} />
                  </Col>
                  <Col md={6}>
                    <Form.Label>Direct Dial</Form.Label>
                    <Form.Control
                      name="direct_dial"
                      value={userForm.direct_dial}
                      onChange={handleChange}
                      placeholder="e.g. 09 622 3908"
                    />
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-3">
              <Card.Body>
                <Card.Title className="h6 text-uppercase text-muted small">Account</Card.Title>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Label>
                      Password {mode === "edit" ? "(leave blank if unchanged)" : ""}
                    </Form.Label>
                    <Form.Control
                      name="password"
                      type="password"
                      value={userForm.password}
                      onChange={handleChange}
                      autoComplete="new-password"
                    />
                    <Form.Text muted>
                      At least 6 characters. Setting a password also unlocks a locked account.
                      Passwords are stored hashed — they can’t be looked up, only reset here.
                    </Form.Text>
                  </Col>
                  {me?.is_admin ? (
                    <Col md={6}>
                      <Form.Label>Groups</Form.Label>
                      <div className="d-flex align-items-center gap-2 flex-wrap">
                        {parseGroups(userForm.member_of).map((g) => (
                          <Badge key={g} bg="primary" className="d-inline-flex align-items-center gap-1">
                            {g}
                            <span
                              role="button"
                              aria-label={`Remove ${g}`}
                              className="ms-1"
                              style={{ cursor: "pointer" }}
                              onClick={() => removeGroup(g)}
                            >
                              ×
                            </span>
                          </Badge>
                        ))}
                        <ThemedSelect
                          size="sm"
                          style={{ width: 180 }}
                          value=""
                          onChange={(e) => addGroup(e.target.value)}
                        >
                          <option value="">Add group…</option>
                          {AVAILABLE_GROUPS.filter(
                            (g) => !parseGroups(userForm.member_of).some((x) => x.toLowerCase() === g.toLowerCase())
                          ).map((g) => (
                            <option key={g} value={g}>{g}</option>
                          ))}
                        </ThemedSelect>
                      </div>
                      <Form.Text muted>
                        Groups drive permissions (like claims 1.0) — Admin = the “Administrator” group.
                      </Form.Text>
                      {/* ACCOUNT STATE, not a role — moved out of the flag pile it
                          used to sit in. Whether somebody can sign in has nothing
                          to do with whether they inspect jobs. */}
                      <div className="mt-3">
                        <Form.Check
                          name="is_active"
                          label="Active (can sign in)"
                          checked={userForm.is_active}
                          onChange={handleChange}
                        />
                        {me?.is_system_owner ? (
                          <Form.Check
                            name="can_mimic"
                            label="Can Mimic other users"
                            checked={userForm.can_mimic}
                            onChange={handleChange}
                          />
                        ) : null}
                      </div>
                    </Col>
                  ) : null}
                </Row>
              </Card.Body>
            </Card>

            {me?.is_admin ? (
              <>
                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title className="h6 text-uppercase text-muted small">Role</Card.Title>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Check
                          className="mb-2"
                          name="is_supervisor"
                          label="Supervisor"
                          checked={userForm.is_supervisor}
                          onChange={handleChange}
                        />
                        <Form.Check
                          name="is_project_coordinator"
                          label="Project Co-Ordinator"
                          checked={userForm.is_project_coordinator}
                          onChange={handleChange}
                        />
                        <Form.Text muted className="d-block">
                          A co-ordinator’s claim search defaults to their own jobs.
                        </Form.Text>
                      </Col>
                      {/* The two "shown in" flags are about where a NAME appears,
                          not what a person does. Under their own heading they stop
                          reading as two more roles. */}
                      <Col md={6}>
                        <div className="small fw-semibold mb-2">Appears in</div>
                        <Form.Check
                          className="mb-2"
                          name="shown_in_project_coordinator"
                          label="Project Co-Ordinator lists"
                          checked={userForm.shown_in_project_coordinator}
                          onChange={handleChange}
                        />
                        <Form.Check
                          name="shown_progress_to_from"
                          label="Progress To/From lists"
                          checked={userForm.shown_progress_to_from}
                          onChange={handleChange}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* A STATUTORY CREDENTIAL, not a permission — its own box because
                    it applies to a handful of people and it is what prints under
                    their name on every quote they sign. The weekend import fills
                    these in from the 1.0 name; anyone licensed since is recorded
                    here by hand. */}
                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title className="h6 text-uppercase text-muted small">
                      Licensed Building Practitioner
                    </Card.Title>
                    <Row className="g-3">
                      <Col md={4}>
                        <Form.Label>LBP Number</Form.Label>
                        <Form.Control
                          name="lbp_number"
                          value={userForm.lbp_number}
                          onChange={handleChange}
                          placeholder="106569"
                        />
                        <Form.Text muted>Digits only — “LBP” is added when it prints.</Form.Text>
                      </Col>
                      <Col md={5}>
                        <Form.Label>Licence Class</Form.Label>
                        <Form.Control
                          name="lbp_class"
                          list="lbp-classes"
                          value={userForm.lbp_class}
                          onChange={handleChange}
                          placeholder="Carpentry"
                        />
                        {/* A datalist, not a closed dropdown: the national set is
                            longer than the one GPS uses and an imported class we
                            have never listed must still be editable. */}
                        <datalist id="lbp-classes">
                          {LBP_CLASSES.map((c) => <option key={c} value={c} />)}
                        </datalist>
                      </Col>
                      <Col md={3} className="d-flex align-items-end">
                        <div className="small text-muted pb-2">
                          Prints as{" "}
                          <strong>
                            {userForm.lbp_number
                              ? `LBP${String(userForm.lbp_number).replace(/\D+/g, "")}${userForm.lbp_class ? ` ${userForm.lbp_class}` : ""}`
                              : "—"}
                          </strong>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                <Card className="mb-3">
                  <Card.Body>
                    <Card.Title className="h6 text-uppercase text-muted small">Reporting</Card.Title>
                    <Row className="g-3">
                      <Col md={6}>
                        <Form.Label>Managed By</Form.Label>
                        <ThemedSelect name="managed_by" value={userForm.managed_by} onChange={handleChange}>
                          <option value="">— Nobody —</option>
                          {users
                            .filter((u) => !editingUser || u.id !== editingUser.id)
                            .map((u) => (
                              <option key={u.id} value={u.id}>{u.full_name}</option>
                            ))}
                        </ThemedSelect>
                      </Col>
                      {/* MANAGES is read-only and wraps. It is derived from other
                          people's Managed By, so editing it here would be a second
                          place to set one fact — and 1.0's version of this list is
                          a wall of dead placeholder accounts precisely because
                          nothing ever tidied it. */}
                      <Col md={6}>
                        <Form.Label>Manages</Form.Label>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          {manages.map((u) => (
                            <Badge key={u.id} bg="primary" className="d-inline-flex align-items-center gap-1">
                              {u.full_name}
                              <span
                                role="button"
                                aria-label={`Remove ${u.full_name}`}
                                className="ms-1"
                                style={{ cursor: "pointer" }}
                                onClick={() => setManagesIds((c) => c.filter((id) => id !== u.id))}
                              >
                                ×
                              </span>
                            </Badge>
                          ))}
                          <ThemedSelect
                            size="sm"
                            style={{ width: 240 }}
                            value=""
                            onChange={(e) => {
                              const id = Number(e.target.value);
                              if (id) setManagesIds((c) => [...c, id]);
                            }}
                          >
                            <option value="">Add person…</option>
                            {manageable.map((u) => (
                              /* Naming their CURRENT manager makes a reassignment a
                                 visible choice. Without it, adding somebody here
                                 quietly takes them off whoever had them, and the
                                 person who loses them never sees it happen. */
                              <option key={u.id} value={u.id}>
                                {u.full_name}
                                {managerNameOf(u) ? ` — currently ${managerNameOf(u)}` : ""}
                              </option>
                            ))}
                          </ThemedSelect>
                        </div>
                        <Form.Text muted>
                          The other side of Managed By. Removing somebody here leaves
                          them reporting to nobody.
                        </Form.Text>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </>
            ) : null}
            <Button
              className="me-2"
              onClick={mode === "add" ? saveAdd : saveEdit}
              disabled={saveDisabled}
            >
              Save
            </Button>
            <Button variant="secondary" onClick={() => setMode("list")}>
              Cancel
            </Button>
          </Form>
        </div>
      )}
    </>
  );
}

export default ManageUsers;
